import React from 'react';

import { hslToRgb } from '../utils/hsl';

import logoImage from '../images/canvasLogoMin.jpg';

const BG_IN_DURATION = 3000;
const BG_IN_OFFSET = -2000;
const LOGO_IN_DURATION = 500;

const MAX_PARTICLES = 20;
const PARTICLE_DELAY = 2000;
const PARTICLES_INTERVAL = 2500;
const EXPLOSION_DURATION = 1000;

const style = {
  nextColor: {
    position: 'absolute',
    bottom: '10px',
    left: '10px',
    right: '10px',
    height: '4px',
  },
};

const getDistance = (p1x, p1y, p2x, p2y) => ((p1x - p2x) ** 2) + ((p1y - p2y) ** 2);

const calcScale = ({ width, height }) => {
  const ref = 1024;
  return Math.min(width / ref, height / ref);
};

const generateBackground = ({ width, height }, scale) => {
  const points = [];
  const inc = 4;
  const distance = 18;

  let cur = 0;
  const maxDistance = Math.sqrt((width * width) + (height * height)) / 2;
  const steps = Math.floor(maxDistance / (distance * scale)) - 1;

  const halfWidth = width / 2;
  const halfHeight = height / 2;

  for (let i = 0; i < steps; i += 1) {
    cur += inc;
    for (let j = 0; j < cur; j += 1) {
      const x = 0;
      const y = distance * (i + 1);
      const angle = ((360 / cur) * j) + 45;

      const radians = (Math.PI / 180) * angle;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);

      const origx = ((cos * x) + (sin * y));
      const origy = ((cos * y) - (sin * x));

      const nx = (origx * scale) + halfWidth;
      const ny = (origy * scale) + halfHeight;

      if (nx > 0 && nx < width && ny > 0 && ny < height) {
        points.push({
          x: nx,
          y: ny,
          origx,
          origy,
          delay: Math.random() * (BG_IN_DURATION / 2),
          duration: Math.random() * (BG_IN_DURATION / 2),
        });
      }
    }
  }

  return points;
};

const generateLogo = ({ width, height }, scale, image, background) => {
  const points = [];

  if (image) {
    const temp = document.createElement('canvas');
    temp.width = image.width;
    temp.height = image.height;
    temp.getContext('2d').drawImage(image, 0, 0, temp.width, temp.height);

    const step = 80;
    const sizeL = 7;

    const halfStep = step / 2;
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const halfTempWidth = temp.width / 2;
    const halfTempHeight = temp.height / 2;

    background.forEach((p) => {
      const x = (p.x - halfWidth) / scale;
      const y = (p.y - halfHeight) / scale;

      if (x > -halfTempWidth && x < halfTempWidth &&
        y > -halfTempHeight && y < halfTempHeight) {
        const pixelData = temp.getContext('2d').getImageData((x + halfTempWidth) - halfStep,
          (y + halfTempHeight) - halfStep, step, step).data;

        let count = 0;
        let total = 0;
        for (let j = 0; j < pixelData.length; j += 4) {
          const t = j / 4;
          const x1 = t % step;
          const y1 = Math.floor(t / step);

          if (getDistance(x1, y1, halfStep, halfStep) < (halfStep ** 2)) {
            total += 1;
            if (pixelData[j] < 128 && pixelData[j + 3] > 128) {
              count += 1;
            }
          }
        }

        const size = count / total;
        if (size > 0) {
          const r = sizeL * size;

          if (r > 1) {
            points.push({
              x: p.x,
              y: p.y,
              origx: p.origx,
              origy: p.origy,
              radiusOriginal: r,
              radius: r,
              size,
              delay: LOGO_IN_DURATION * Math.random(),
              color: {
                r: 200,
                g: 200,
                b: 200,
              },
            });
          }
        }
      }
    });
  }
  return points;
};

const repositionLogo = (points, scale, width, height) => {
  const halfHeight = height / 2;
  const halfWidth = width / 2;

  points.forEach((point) => {
    const p = point;
    p.radius = p.radiusOriginal * scale;
    p.x = (p.origx * scale) + halfWidth;
    p.y = (p.origy * scale) + halfHeight;
  });
};

const randomColor = () => hslToRgb(Math.random(), 0.75, 0.65);

class Header extends React.Component {
  constructor() {
    super();

    this.context = null;
    this.canvas = null;

    this.logoImage = null;
    this.logoCanvas = null;

    this.startTime = null;
    this.lastUpdateTime = null;

    this.particleInterval = null;

    this.state = {
      animState: 0,
      //
      size: {
        width: 0,
        height: 0,
        ratio: typeof (window) !== 'undefined' ? window.devicePixelRatio || 1 : 1,
      },
      scale: 1.0,
      nextColor: randomColor(),
    };

    this.background = [];
    this.logo = [];
    this.particles = [];

    this.shots = 0;

    this.animFrameID = 0;

    this.handleClick = this.handleClick.bind(this);
  }

  componentDidMount() {
    if (typeof (window) !== 'undefined') {
      setTimeout(() => {
        this.startHeader();
        this.setState({
          animState: 1,
        });
      }, 1250);
    }
  }

  componentWillUnmount() {
    cancelAnimationFrame(this.animFrameID);

    if (this.particleInterval) {
      clearInterval(this.particleInterval);
    }

    if (typeof (window) !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  setNextColor() {
    this.setState({ nextColor: randomColor() });
  }

  startHeader() {
    const image = new Image();
    image.onload = () => {
      this.logoImage = image;

      this.logo = generateLogo({
        width: this.state.size.width,
        height: this.state.size.height,
      }, this.state.scale, this.logoImage, this.background);

      repositionLogo(this.logo, this.state.scale, this.state.size.width, this.state.size.height);
    };
    image.src = logoImage;

    if (typeof (window) !== 'undefined') {
      window.addEventListener('resize', this.handleResize.bind(this, false));
    }

    this.handleResize();

    this.context = this.canvas.getContext('2d');

    this.startTime = new Date().getTime();

    this.setNextColor();

    this.animFrameID = requestAnimationFrame(() => this.update());
  }

  handleResize() {
    if (this.canvas) {
      const rect = this.canvas.parentNode.getBoundingClientRect();
      const scale = calcScale(rect);

      const background = generateBackground(rect, scale);

      this.setState({
        size: {
          width: rect.width,
          height: rect.height,
          ratio: typeof (window) !== 'undefined' ? window.devicePixelRatio || 1 : 1,
        },
        scale,
      });

      this.background = background;
      repositionLogo(this.logo, scale, rect.width, rect.height);
    }
  }

  update() {
    const now = new Date().getTime();
    const dt = now - this.startTime;

    const ctx = this.context;

    ctx.clearRect(0, 0, this.state.size.width, this.state.size.height);

    this.drawBackground(ctx, dt);

    if (dt > BG_IN_DURATION + BG_IN_OFFSET) {
      this.drawLogo(ctx, dt);
    }

    if (dt > BG_IN_DURATION + PARTICLE_DELAY) {
      if (!this.particleInterval) {
        this.particleInterval = setTimeout(() => {
          this.fireParticle();
          this.particleInterval = null;
        }, PARTICLES_INTERVAL);
      }

      this.updateParticles(now);

      this.drawExplosion(ctx, now);

      this.particles = this.particles.filter(p => p.done < 2);

      this.drawParticle(ctx);
    }

    this.lastUpdateTime = now;

    // Next frame
    this.animFrameID = requestAnimationFrame(() => this.update());
  }

  updateParticles(now) {
    const scale = this.state.scale;
    const { width, height } = this.state.size;

    const logoBounds = {
      x: (330 * scale) + (width / 2),
      y: (270 * scale) + (height / 2),
    };

    // update
    this.particles.forEach((particle) => {
      const p = particle;
      if (p.done === 0) {
        p.pos.x += p.direction.x;
        p.pos.y += p.direction.y;

        if (p.pos.x < 0 || p.pos.x > width) {
          p.direction.x *= -1;
        }
        if (p.pos.y < 0 || p.pos.y > height) {
          p.direction.y *= -1;
        }

        if (p.pos.x > -logoBounds.x && p.pos.x < logoBounds.x
          && p.pos.y > -logoBounds.y && p.pos.y < logoBounds.y) {
          let coll = false;
          this.logo.forEach((p1) => {
            const dx = p1.x - p.pos.x;
            const dy = p1.y - p.pos.y;

            let collDistance = p1.radius + (3 * scale);
            if (p.isReset) {
              collDistance += 3 * scale * 0.5;
            }

            coll = coll ||
              Math.sqrt((dx ** 2) + (dy ** 2)) < collDistance;
          });
          if (coll) {
            p.done = 1;
            p.doneStart = now;
            this.logo.forEach((particleLogo) => {
              const p2 = particleLogo;
              const dx = p2.x - p.pos.x;
              const dy = p2.y - p.pos.y;

              const dist = Math.sqrt((dx ** 2) + (dy ** 2));
              let relDist = dist / (100 * scale);

              if (p.isReset) {
                relDist = Math.max(0, relDist - 0.5);
              }

              const invRelDist = 1.0 - relDist;
              if (dist < 100 * scale) {
                p2.color.r = Math.floor(((p.color.r * invRelDist) + (p2.color.r * relDist)));
                p2.color.g = Math.floor(((p.color.g * invRelDist) + (p2.color.g * relDist)));
                p2.color.b = Math.floor(((p.color.b * invRelDist) + (p2.color.b * relDist)));
              }
            });
          }
        }
      }
    });
  }

  drawBackground(ctx, dt) {
    const scale = this.state.scale;

    if (dt > BG_IN_DURATION) {
      ctx.fillStyle = 'rgb(64,64,64)';
    }

    this.background.forEach((p) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(1, 1 * scale), 0, 2 * Math.PI, false);
      ctx.closePath();

      if (dt <= BG_IN_DURATION) {
        let alpha = 1.0;
        if (dt < p.delay) {
          alpha = 0.0;
        } else if (dt < p.delay + p.duration) {
          alpha = (dt - p.delay) / p.duration;
        }

        ctx.fillStyle = `rgba(64,64,64,${alpha})`;
      }
      ctx.fill();
    });
  }

  drawLogo(ctx, dt) {
    const timeStep1 = BG_IN_DURATION + BG_IN_OFFSET;
    const timeStep2 = timeStep1 + LOGO_IN_DURATION + 500;
    this.logo.forEach((p) => {
      ctx.beginPath();

      if (dt > timeStep2) {
        ctx.arc(p.x, p.y, p.radius, 0, 2 * Math.PI, false);
        ctx.fillStyle = `rgba(${p.color.r},${p.color.g},${p.color.b}, ${p.size})`;
      } else {
        let alphaMod = 1.0;
        if (dt < timeStep1 + p.delay) {
          alphaMod = 0.0;
        } else if (dt < timeStep1 + p.delay + 500) {
          alphaMod = (dt - (timeStep1 + p.delay)) / 500;
        }
        ctx.arc(p.x, p.y, p.radius * alphaMod, 0, 2 * Math.PI, false);
        ctx.fillStyle = `rgba(200,200,200, ${p.size * alphaMod})`;
      }

      ctx.closePath();
      ctx.fill();
    });
  }

  drawExplosion(ctx, now) {
    const angleVar = 360 / 6;
    const steps = 10;
    const anglePerStep = angleVar / steps;

    const scale = this.state.scale;

    this.particles.forEach((particle) => {
      const p = particle;
      if (p.done === 1) {
        if (now < p.doneStart + EXPLOSION_DURATION) {
          const progress = (now - p.doneStart) / EXPLOSION_DURATION;
          for (let i = 0; i < 6; i += 1) {
            const angle = ((angleVar * i) + (anglePerStep * (p.pos.x % steps))) * (Math.PI / 180);
            const s = Math.sin(angle);
            const c = Math.cos(angle);
            const x = -30 * scale * progress * s;
            const y = 30 * scale * progress * c;

            ctx.beginPath();
            ctx.arc(p.pos.x + x, p.pos.y + y, scale * (3 + (6 * progress)), 0, 2 * Math.PI, false);
            ctx.strokeStyle = `rgba(${p.color.r},${p.color.g},${p.color.b},${0.5 * (1 - progress)})`;
            ctx.lineWidth = 2 * scale;
            ctx.closePath();
            ctx.stroke();
          }
        } else {
          p.done = 2;
        }
      }
    });
  }

  drawParticle(ctx) {
    const size = this.state.scale * 3;

    this.particles.forEach((p) => {
      if (p.done === 0) {
        ctx.beginPath();
        if (p.isReset) {
          ctx.arc(p.pos.x, p.pos.y, size * 1.5, 0, 2 * Math.PI, false);
        } else {
          ctx.arc(p.pos.x, p.pos.y, size, 0, 2 * Math.PI, false);
        }
        ctx.closePath();
        if (p.isReset) {
          ctx.strokeStyle = `rgb(${p.color.r},${p.color.g},${p.color.b})`;
          ctx.stroke();
        } else {
          ctx.fillStyle = `rgb(${p.color.r},${p.color.g},${p.color.b})`;
          ctx.fill();
        }
      }
    });
  }

  fireParticle(startPos) {
    if (startPos || this.particles.length < MAX_PARTICLES) {
      let pos;
      let direction;

      let color;
      let isReset = false;

      if (startPos) {
        pos = startPos;

        direction = {
          x: (this.state.size.width / 2) - startPos.x,
          y: (this.state.size.height / 2) - startPos.y,
        };

        color = this.state.nextColor;
        this.setNextColor();
      } else {
        pos = {
          x: Math.random() * this.state.size.width,
          y: Math.random() * this.state.size.height,
        };

        direction = {
          x: (-0.5 + Math.random()),
          y: (-0.5 + Math.random()),
        };

        if (Math.abs(direction.x) < 0.05) {
          direction.x = (0.5 - 0.05) - direction.x;
        }
        if (Math.abs(direction.y) < 0.05) {
          direction.y = (0.5 - 0.05) - direction.y;
        }

        if (Math.random() < 0.5) {
          if (direction.x < 0) {
            pos.x = this.state.size.width;
          } else {
            pos.x = 0.0;
          }
        } else if (direction.y < 0) {
          pos.y = this.state.size.height;
        } else {
          pos.y = 0.0;
        }

        if (this.shots > 25 && this.shots % 4 === 0) {
          color = {
            r: 200,
            g: 200,
            b: 200,
          };
          isReset = true;
        } else {
          color = randomColor();
        }
      }

      const speed = 8 * this.state.scale;

      const len = Math.sqrt((direction.x * direction.x) + (direction.y * direction.y));
      direction.x = (direction.x * speed) / len;
      direction.y = (direction.y * speed) / len;

      const variance = 6 * this.state.scale;

      if (startPos) {
        direction.x += ((variance * Math.random()) - (variance / 2));
        direction.y += ((variance * Math.random()) - (variance / 2));
      }

      this.particles.push({
        pos,
        color,
        direction,
        done: 0,
        doneStart: 0,
        isReset,
      });

      this.shots += 1;
    }
  }

  handleClick(e) {
    this.fireParticle({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
  }

  render() {
    if (typeof (window) === 'undefined') {
      return null;
    }

    const col = this.state.nextColor;
    return (
      <header aria-hidden="true">
        { this.state.animState === 0 ? (<div className="bg" />) : null }
        <canvas
          ref={(c) => { this.canvas = c; }}
          width={this.state.size.width}
          height={this.state.size.height}
          onClick={this.handleClick}
        />
        <div className="nextColor" style={{ ...style.nextColor, backgroundColor: `rgb(${col.r},${col.g},${col.b})` }} />
      </header>
    );
  }
}

export default Header;



// WEBPACK FOOTER //
// ./src/components/header.jsx